import React, { useState, useContext, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { useLinkedInAuth } from "../context/LinkedInAuthProvider";
import { SessionContext } from "../context/sessionContext";
import { FaEye, FaEyeSlash, FaInfoCircle } from "react-icons/fa";
import Cookies from "js-cookie";
import EmailVerification from "../components/emailverification";

const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [inviteData, setInviteData] = useState(null);
  const { loginWithLinkedIn } = useLinkedInAuth();
  const { socialLogin, setSession } = useContext(SessionContext);
  const [loading, setLoading] = useState({ google: false, linkedin: false });

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingSignupData, setPendingSignupData] = useState(null);
  const passwordRules = {
    length: formData.password.length >= 8 && formData.password.length <= 16,
    upper: /[A-Z]/.test(formData.password),
    lower: /[a-z]/.test(formData.password),
    number: /[0-9]/.test(formData.password),
    special: /[!@#$%^&*]/.test(formData.password),
  };
  useEffect(() => {
    const setupInviteData = async () => {
      const token = searchParams.get("activateLoginToken");
      const orgName = searchParams.get("org_name");
      const invitedUserId = searchParams.get("invitedUserId");
      const role = searchParams.get("role");
      const plan = searchParams.get("plan");
      const paid = searchParams.get("paid");
      if (token || orgName || invitedUserId || role || plan) {
        const inviteDataObj = {
          token: token || null,
          org_name: orgName || null,
          invitedUserId: invitedUserId || null,
          role: role || "Researcher",
          plan: plan || "Growth",
          paymentIncomplete: plan === "Custom" && paid === "0",
        };
        if (
          inviteDataObj.plan === "Freemium" ||
          inviteDataObj.plan === "Custom"
        ) {
          inviteDataObj.role = "admin";
        }
        setInviteData(inviteDataObj);
        localStorage.setItem(
          "pendingInviteData",
          JSON.stringify(inviteDataObj),
        );
        if (inviteDataObj.email) {
          setFormData((prev) => ({ ...prev, email: inviteDataObj.email }));
        }
      }
    };

    setupInviteData();
  }, [searchParams]);
  const getFirstNameFromEmail = (email) => {
    const username = email.split("@")[0];
    const cleanName = username.replace(/[^a-zA-Z]/g, "");
    return cleanName.charAt(0).toUpperCase() + cleanName.slice(1).toLowerCase();
  };
  const validate = () => {
    const errs = {};
    if (!formData.email) {
      errs.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      errs.email = "Enter a valid email address";
    }

    if (!formData.password) {
      errs.password = "Password is required";
    } else if (formData.password.length < 8 || formData.password.length > 16) {
      errs.password = "Password must be between 8 and 16 characters";
    } else if (!/[a-z]/.test(formData.password)) {
      errs.password = "Password must contain at least one lowercase letter";
    } else if (!/[A-Z]/.test(formData.password)) {
      errs.password = "Password must contain at least one uppercase letter";
    } else if (!/[0-9]/.test(formData.password)) {
      errs.password = "Password must contain at least one digit";
    } else if (!/[!@#$%^&*]/.test(formData.password)) {
      errs.password =
        "Password must contain at least one special character (!@#$%^&*)";
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: validate()[name] }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validate());
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setErrors({});
    const errs = validate();
    setErrors(errs);
    setTouched({ email: true, password: true });

    if (Object.keys(errs).length > 0) return;
    const rawSelectedPlan = localStorage.getItem("selectedPlan");
    const selectedPlanData = rawSelectedPlan
      ? JSON.parse(rawSelectedPlan)
      : null;
    const isEduEmail = (email) =>
      /\.(edu|ac\.uk|ac\.in|edu\.in|edu\.au|edu\.sg|ac\.za|edu\.pk)$/i.test(
        email,
      );
    if (
      selectedPlanData?.planName === "Student" &&
      !isEduEmail(formData.email) &&
      !inviteData
    ) {
      setErrors({
        email: "Student plan requires a valid academic email address.",
      });
      return;
    }

    setApiLoading(true);

    try {
      const firstName = getFirstNameFromEmail(formData.email);

      const signupPayload = {
        first_name: firstName,
        email: formData.email,
        password: formData.password,
        role: inviteData?.role || "admin",
        invitedUserId: inviteData?.invitedUserId || null,
      };

      const signupRes = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/signup`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(signupPayload),
        },
      );

      const signupJson = await signupRes.json();

      if (!signupRes.ok) {
        const msg = signupJson.message || signupJson.error || "Signup failed";
        if (
          msg.toLowerCase().includes("email") ||
          msg.toLowerCase().includes("exists")
        ) {
          setErrors({ email: msg });
        } else {
          setErrors({ api: msg });
        }
        return;
      }

      if (signupJson.isNewUser !== true) {
        setErrors({
          email:
            "An account with this email already exists. Please log in instead.",
        });
        return;
      }

      if (!signupJson.token) {
        setErrors({
          api: signupJson.message || "Signup did not return a token",
        });
        return;
      }
      const otpResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/otp/send-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            identifier: formData.email,
            first_name: firstName,
          }),
        },
      );

      if (!otpResponse.ok) {
        const otpData = await otpResponse.json();
        setErrors({
          api:
            otpData.message ||
            "Failed to send verification code. Please try again.",
        });
        return;
      }
      sessionStorage.setItem("pendingSignupFlow", "true");

      setPendingSignupData({
        token: signupJson.token,
        email: formData.email,
        firstName: firstName,
        inviteData: inviteData,
      });
      setShowVerificationModal(true);
    } catch (err) {
      console.error("Signup error:", err);
      setErrors({ api: "Server error. Please try again later." });
    } finally {
      setApiLoading(false);
    }
  };
  const handleEmailVerified = async () => {
    try {
      const { token, email, firstName, inviteData } = pendingSignupData;
      const verifyRes = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/verifyfirsttime`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        },
      );

      const verifyJson = await verifyRes.json();

      if (!verifyRes.ok || !verifyJson.user) {
        setErrors({
          api: verifyJson.message || "Verification failed after signup",
        });
        setShowVerificationModal(false);
        return;
      }

      const user = verifyJson.user;
      user.user_id = user.user_id || user.id || user.userId || user._id || null;
      if (!user.id && user.user_id) user.id = user.user_id;
      Cookies.set("token", token, { expires: 2 });

      setSession({
        token: token,
        user,
        hasCompletedCreatorFlow: false,
      });

      localStorage.setItem("creatorFlowDone", "false");
      try {
        localStorage.setItem(
          "userSession",
          JSON.stringify({ token: token, user }),
        );
      } catch {}

      if (inviteData) {
        localStorage.setItem("pendingInviteData", JSON.stringify(inviteData));
      }
      setShowVerificationModal(false);

      navigate("/create-org", {
        state: {
          isEmailSignup: true,
          signupData: { email: email },
          userName: firstName,
          inviteData: inviteData,
        },
      });
    } catch (err) {
      console.error("Post-verification error:", err);
      setErrors({ api: "Verification failed. Please try again." });
      setShowVerificationModal(false);
    }
  };
  const handleVerificationModalClose = () => {
    setShowVerificationModal(false);
    setPendingSignupData(null);
    sessionStorage.removeItem("pendingSignupFlow");
  };
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (codeResponse) => {
      setLoading((prev) => ({ ...prev, google: true }));

      try {
        sessionStorage.setItem("pendingSignupFlow", "true");

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/google-code`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code: codeResponse.code }),
          },
        );

        const data = await response.json();

        if (response.ok && data.token) {
          const user = data.user || {
            email: data.email || data.data?.email,
            name: data.name || data.data?.name,
          };

          const loginResult = await socialLogin(data.token, user);
          if (!loginResult.success) {
            sessionStorage.removeItem("pendingSignupFlow");
            alert("Authentication failed. Please try again.");
            return;
          }

          if (data.isNewUser || inviteData) {
            if (inviteData) {
              localStorage.setItem(
                "pendingInviteData",
                JSON.stringify(inviteData),
              );
            }

            navigate("/create-org", {
              state: {
                isEmailSignup: false,
                socialToken: data.token,
                userName: user.name,
                inviteData: inviteData,
              },
            });
          } else {
            sessionStorage.removeItem("pendingSignupFlow");
            navigate("/projects");
          }
        } else {
          sessionStorage.removeItem("pendingSignupFlow");
          alert(data.message || "Google authentication failed.");
        }
      } catch (error) {
        console.error("Google auth error:", error);
        sessionStorage.removeItem("pendingSignupFlow");
        alert("An error occurred. Please try again.");
      } finally {
        setLoading((prev) => ({ ...prev, google: false }));
      }
    },
    onError: (error) => {
      console.error("Google login error:", error);
      sessionStorage.removeItem("pendingSignupFlow");
      setLoading((prev) => ({ ...prev, google: false }));
    },
  });
  const handleLinkedInLogin = () => {
    setLoading((prev) => ({ ...prev, linkedin: true }));
    sessionStorage.setItem("pendingSignupFlow", "true");
    localStorage.setItem("linkedin_signup_flow", "true");

    if (inviteData) {
      localStorage.setItem("pendingInviteData", JSON.stringify(inviteData));
    }

    loginWithLinkedIn(inviteData);
  };

  return (
    <div className="page-bg">
      <div className="create-survey-logo">
        <Link to="/">
          <img src="/json-media/img/logo.svg" width={50} alt="GI" />
        </Link>
      </div>
      <div className="content-center">
        <div className="signup-container">
          <div className="signup-header">
            <h3>Sign up</h3>
          </div>
          {inviteData?.paymentIncomplete && (
            <p
              className="error-text"
              style={{ textAlign: "center", marginBottom: "1rem" }}
            >
              It looks like your payment wasn't completed. You can finish
              creating your account below, but it won't be activated until
              payment goes through — check your invite email for the payment
              link, or contact us if you need it resent.
            </p>
          )}
          <form className="signup-form" onSubmit={handleEmailSubmit} noValidate>
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={inviteData?.email}
              />
              {touched.email && errors.email && (
                <p className="error-text">{errors.email}</p>
              )}
            </div>
            <div className="form-group password-group">
              <label>Password</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Add a password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <span
                  className="toggle-eye"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </span>
                <div className="info-wrapper">
                  <FaInfoCircle className="info-icon" />
                  <div className="tooltip">
                    <ul className="password-rules">
                      <li className={passwordRules.length ? "valid" : ""}>
                        8–16 characters
                      </li>
                      <li className={passwordRules.lower ? "valid" : ""}>
                        At least 1 lowercase letter (a–z)
                      </li>
                      <li className={passwordRules.upper ? "valid" : ""}>
                        At least 1 uppercase letter (A–Z)
                      </li>
                      <li className={passwordRules.number ? "valid" : ""}>
                        At least 1 number (0–9)
                      </li>
                      <li className={passwordRules.special ? "valid" : ""}>
                        At least 1 special character (!@#$%^&*)
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              {touched.password && errors.password && (
                <p className="error-text">{errors.password}</p>
              )}
            </div>
            {errors.api && (
              <p className="error-text" style={{ textAlign: "center" }}>
                {errors.api}
              </p>
            )}
            <button type="submit" className="signup-btn" disabled={apiLoading}>
              {apiLoading ? "Creating account..." : "Create Account"}
            </button>
          </form>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              margin: "1.5rem 0",
              color: "#94a3b8",
              fontSize: "0.875rem",
            }}
          >
            <div
              style={{ flex: 1, height: "1px", background: "#374151" }}
            ></div>
            <span style={{ padding: "0 1rem" }}>Or sign up with</span>
            <div
              style={{ flex: 1, height: "1px", background: "#374151" }}
            ></div>
          </div>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <button
              className="social-btn"
              onClick={() => googleLogin()}
              disabled={loading.google || loading.linkedin}
            >
              <span className="icon">
                <img src="/json-media/icons/google.svg" alt="google" />
              </span>
              {loading.google ? "Connecting..." : "Google"}
            </button>
            <button
              className="social-btn"
              onClick={handleLinkedInLogin}
              disabled={loading.google || loading.linkedin}
            >
              <span className="icon">
                <img src="/json-media/icons/linkedin.svg" alt="linkedin" />
              </span>
              {loading.linkedin ? "Connecting..." : "LinkedIn"}
            </button>
          </div>
          <p
            style={{
              marginTop: "1.5rem",
              textAlign: "center",
              fontSize: "0.95rem",
            }}
          >
            Already have an account?{" "}
            <Link to="/log-in" style={{ color: "rgb(59, 130, 246)" }}>
              Log in
            </Link>
          </p>
        </div>
      </div>
      {showVerificationModal && pendingSignupData && (
        <EmailVerification
          open={showVerificationModal}
          email={pendingSignupData.email}
          firstName={pendingSignupData.firstName}
          onVerified={handleEmailVerified}
          onClose={handleVerificationModalClose}
        />
      )}
    </div>
  );
};

export default SignupPage;
